import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface BuildInfo {
  name?: string;
  version?: string;
  buildNumber?: string;
  commit?: string;
  builtAt?: string;
}

export interface FooterBuildInfo {
  clientLabel: string;
  serverLabel: string;
}

@Injectable({
  providedIn: 'root'
})
export class BuildInfoService {
  private readonly http = inject(HttpClient);

  getFooterBuildInfo(): Observable<FooterBuildInfo> {
    return forkJoin({
      client: this.getClientBuildInfo().pipe(catchError(() => of(this.defaultClientBuildInfo()))),
      server: this.getServerBuildInfo().pipe(catchError(() => of(null)))
    }).pipe(
      map(({ client, server }) => ({
        clientLabel: `Client ${this.formatBuildInfo(client)}`,
        serverLabel: server ? `Server ${this.formatBuildInfo(server)}` : 'Server unavailable'
      }))
    );
  }

  getClientBuildInfo(): Observable<BuildInfo> {
    return this.http.get<BuildInfo>('assets/build-info.json');
  }

  getServerBuildInfo(): Observable<BuildInfo> {
    return this.http.get<BuildInfo>(`${this.resolveApiBase()}/build-info`, {
      withCredentials: true
    });
  }

  private defaultClientBuildInfo(): BuildInfo {
    return {
      name: 'crd-client',
      version: 'unknown',
      buildNumber: 'local',
      commit: 'unknown',
      builtAt: 'unknown'
    };
  }

  private formatBuildInfo(buildInfo: BuildInfo): string {
    const version = buildInfo.version || 'unknown';
    const buildNumber = buildInfo.buildNumber || 'local';

    return `${version}+${buildNumber}`;
  }

  private resolveApiBase(): string {
    const serviceBase = environment.SERVICE_BASE || window.location.origin;
    const normalizedBase = serviceBase.replace(/\/$/, '');
    const serviceContext = environment.SERVICE_CONTEXT || '/api';
    const normalizedContext = serviceContext.startsWith('/')
      ? serviceContext
      : `/${serviceContext}`;

    return `${normalizedBase}${normalizedContext}`;
  }
}
